import { useEffect, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';

type TimeMachineNowResponse = { utc_now: string };

let cachedUtcNow: Date | null = null;
let cachedAtMs = 0;

// Keep this small to minimize drift while avoiding chatty calls.
const CACHE_TTL_MS = 60_000;

function parseIsoDate(value: string | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function refreshTimeMachineUtcNowCache() {
  cachedUtcNow = null;
  cachedAtMs = 0;
}

export function useTimeMachineUtcNow() {
  const [utcNow, setUtcNow] = useState<Date | null>(() => {
    if (cachedUtcNow && Date.now() - cachedAtMs < CACHE_TTL_MS) return cachedUtcNow;
    return null;
  });
  const [loading, setLoading] = useState<boolean>(utcNow == null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (cachedUtcNow && Date.now() - cachedAtMs < CACHE_TTL_MS) {
        setUtcNow(cachedUtcNow);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = (await fetchWrapper.get(
          urls.DevOps.getTimeMachineUtcNow,
        )) as TimeMachineNowResponse;
        const parsed = parseIsoDate(data?.utc_now);
        if (!parsed) throw new Error('Invalid timemachine utc_now response');

        cachedUtcNow = parsed;
        cachedAtMs = Date.now();

        if (!cancelled) setUtcNow(parsed);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load timemachine date');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    function handleTimeMachineChanged() {
      refreshTimeMachineUtcNowCache();
      void load();
    }

    load();
    window.addEventListener('timemachine:changed', handleTimeMachineChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('timemachine:changed', handleTimeMachineChanged);
    };
  }, []);

  return { utcNow, loading, error };
}

